import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { SearchProvider } from "../context/SearchContext";
import { UserProvider } from "../context/UserContext";
import { OpenInnovationWithAuthorization, WithAuthorization, WithoutAuthorization } from "./Authorization";

// Landing
const Landing = lazy(() => import("pages/landing"));

// 로그인
const Login = lazy(() => import("pages/user/Login"));
const SignUp = lazy(() => import("pages/user/signup"));
const Find = lazy(() => import("pages/user/find"));
const SendEmail = lazy(() => import("pages/user/find/Email"));
const EnterNew = lazy(() => import("pages/user/find/EnterNew"));
const Welcome = lazy(() => import("pages/user/Welcome"));

// 이용약관
const TermsOfUse = lazy(() => import("pages/terms/TermsOfUse"));
const PrivacyPolicy = lazy(() => import("pages/terms/PrivacyPolicy"));

// 문의
const Inquiry = lazy(() => import("pages/inquiry/Inquiry"));

// feature
const Features = lazy(() => import("pages/features/Landing"));

// 기업
const Layout = lazy(() => import("layout/Layout"));
const CompanyDetailLayout = lazy(() => import("layout/CompanyDetailLayout"));
const CompanyList = lazy(() => import("pages/company/List"));
const Summary = lazy(() => import("pages/company/detail/Summary"));
const Finance = lazy(() => import("pages/company/detail/Finance"));
const Network = lazy(() => import("pages/company/detail/Network"));
const AI = lazy(() => import("pages/company/detail/AI"));
const Contact = lazy(() => import("pages/company/detail/Contact"));
const Product = lazy(() => import("pages/company/detail/Product"));
const ProductDetail = lazy(() => import("pages/company/detail/ProductDetail"));
const CompanyDetailNews = lazy(() => import("pages/company/detail/News"));

// 특허
const PatentList = lazy(() => import("pages/patent/List"));
const PatentDetail = lazy(() => import("pages/patent/detail/index"));

// 논문
const PaperList = lazy(() => import("pages/paper/List"));
const PaperDetail = lazy(() => import("pages/paper/detail/index"));

//이미지
const Image = lazy(() => import("pages/image/List"));

// 뉴스
const News = lazy(() => import("pages/news/List"));

//경쟁사
const CompetitorLanding = lazy(() => import("pages/competitor/Landing"));
const CompetitorLayout = lazy(() => import("layout/CompetitorLayout"));
const CompanyComparison = lazy(() => import("pages/competitor/CompanyComparison"));
const EmploymentComparison = lazy(() => import("pages/competitor/EmployementComparison"));
const FinancialComparison = lazy(() => import("pages/competitor/FinancialComparison"));
const TechnologyComparison = lazy(() => import("pages/competitor/TechnologyComparison"));
const NewsAnalysis = lazy(() => import("pages/competitor/NewsAnalysis/index"));
const View = lazy(() => import("pages/competitor/setCompany/View"));
const Edit = lazy(() => import("pages/competitor/setCompany/Edit"));

// 해외기업
const OverseasLanding = lazy(() => import("pages/overseas/Landing"));
const VietnamList = lazy(() => import("pages/overseas/vietnam/List"));
const VietnamInterest = lazy(() => import("pages/overseas/vietnam/Interest"));

// 오픈이노베이션
const OpenInnovationLanding = lazy(() => import("pages/openInnovation/Landing"));
const OpenInnovationLayout = lazy(() => import("layout/OpenInnovationLayout"));
const Dashboard = lazy(() => import("pages/openInnovation/Dashboard"));
const Faq = lazy(() => import("pages/openInnovation/faq/List"));
const BookMark = lazy(() => import("pages/openInnovation/bookmark/List"));
const TechnologyList = lazy(() => import("pages/openInnovation/technology/List"));
const TechnologyDetail = lazy(() => import("pages/openInnovation/technology/Detail"));
const Suggest = lazy(() => import("pages/openInnovation/technology/suggest"));
const Register = lazy(() => import("pages/openInnovation/technology/register"));
const EnterpriseList = lazy(() => import("pages/openInnovation/enterprise/List/index"));
const EnterpriseEdit = lazy(() => import("pages/openInnovation/enterprise/Edit"));
const EnterpriseAdd = lazy(() => import("pages/openInnovation/enterprise/Add"));
const Content = lazy(() => import("pages/openInnovation/enterprise/Content"));
const EnterpriseDetail = lazy(() => import("pages/openInnovation/enterprise/Detail"));
const NoticeList = lazy(() => import("pages/openInnovation/notice/List"));
const NoticeDetail = lazy(() => import("pages/openInnovation/notice/Detail"));
const ManagementList = lazy(() => import("pages/openInnovation/management/List"));
const ManagementDetail = lazy(() => import("pages/openInnovation/management/Detail"));
const TechnologyView = lazy(() => import("pages/openInnovation/management/technology/View"));
const TechnologyEdit = lazy(() => import("pages/openInnovation/management/technology/Edit"));
const ProposalView = lazy(() => import("pages/openInnovation/management/proposal/View"));
const ProposalEdit = lazy(() => import("pages/openInnovation/management/proposal/Edit"));

// 마이페이지
const MyPageLayout = lazy(() => import("layout/MyPageLayout"));
const Profile = lazy(() => import("pages/mypage/profile/View"));
const ProfileEdit = lazy(() => import("pages/mypage/profile/Edit"));
const Password = lazy(() => import("pages/mypage/password"));
const Message = lazy(() => import("pages/mypage/message/List"));
const MessageView = lazy(() => import("pages/mypage/message/View"));
const MessageManagement = lazy(() => import("pages/mypage/messageManagement/List"));
const MessageManagementView = lazy(() => import("pages/mypage/messageManagement/View"));
const Interest = lazy(() => import("pages/mypage/interest/List"));
const MyCompany = lazy(() => import("pages/mypage/company/View"));
const MyCompanyEdit = lazy(() => import("pages/mypage/company/Edit"));
const MyProduct = lazy(() => import("pages/mypage/product/List"));
const MyProductEdit = lazy(() => import("pages/mypage/product/Edit"));
const Invite = lazy(() => import("pages/mypage/invite"));
const Authority = lazy(() => import("pages/mypage/authority"));

const Routing = () => {
    return (
        <Router>
            <UserProvider>
                <SearchProvider>
                    <Suspense fallback={<div className="loading" />}>
                        <Routes>
                            <Route path="/" element={<Landing />} />
                            <Route
                                path="/login"
                                element={
                                    <WithoutAuthorization restricted>
                                        <Login />
                                    </WithoutAuthorization>
                                }
                            />
                            <Route
                                path="/signup"
                                element={
                                    <WithoutAuthorization restricted>
                                        <SignUp />
                                    </WithoutAuthorization>
                                }
                            />
                            <Route
                                path="/openInnovation/signup"
                                element={
                                    <WithoutAuthorization restricted>
                                        <SignUp isOpenInnovation />
                                    </WithoutAuthorization>
                                }
                            />
                            <Route
                                path="/find"
                                element={
                                    <WithoutAuthorization restricted>
                                        <Find />
                                    </WithoutAuthorization>
                                }
                            />
                            <Route
                                path="/send_email"
                                element={
                                    <WithoutAuthorization restricted>
                                        <SendEmail />
                                    </WithoutAuthorization>
                                }
                            />
                            <Route
                                path="/enter_new"
                                element={
                                    <WithoutAuthorization restricted>
                                        <EnterNew />
                                    </WithoutAuthorization>
                                }
                            />
                            <Route
                                path="/welcome"
                                element={
                                    <WithoutAuthorization restricted>
                                        <Welcome />
                                    </WithoutAuthorization>
                                }
                            />

                            {/* 기업 */}
                            <Route path="/company" element={<Layout />}>
                                <Route path="list" element={<CompanyList />} />
                                <Route
                                    path="detail"
                                    element={
                                        <WithAuthorization>
                                            <CompanyDetailLayout />
                                        </WithAuthorization>
                                    }
                                >
                                    <Route path="summary" element={<Summary />} />
                                    <Route path="network" element={<Network />} />
                                    <Route path="finance" element={<Finance />} />
                                    <Route path="ai" element={<AI />} />
                                    <Route path="contact" element={<Contact />} />
                                    <Route path="product" element={<Product />} />
                                    <Route path="product-detail" element={<ProductDetail />} />
                                    <Route path="news" element={<CompanyDetailNews />} />
                                </Route>
                            </Route>

                            {/* 특허 */}
                            <Route path="/patent" element={<Layout />}>
                                <Route path="" element={<PatentList />} />
                                <Route
                                    path=":patentId"
                                    element={
                                        <WithAuthorization>
                                            <PatentDetail />
                                        </WithAuthorization>
                                    }
                                />
                                <Route path="*" element={<Navigate to="/patent" />} />
                            </Route>

                            {/* 논문 */}
                            <Route path="/paper" element={<Layout />}>
                                <Route path="" element={<PaperList />} />
                                <Route
                                    path=":paperId"
                                    element={
                                        <WithAuthorization>
                                            <PaperDetail />
                                        </WithAuthorization>
                                    }
                                />
                                <Route path="*" element={<Navigate to="/paper" />} />
                            </Route>

                            {/* 이미지 */}
                            <Route path="/image/*" element={<Image />} />

                            {/* 뉴스 */}
                            <Route path="/news/*" element={<News />} />

                            {/* 이용약관 */}
                            <Route path="/terms" element={<Layout noNav={true} />}>
                                <Route path="use" element={<TermsOfUse />} />
                                <Route path="privacy" element={<PrivacyPolicy />} />
                                <Route path="*" element={<Navigate to="/terms/use" />} />
                            </Route>

                            {/* 문의하기 */}
                            <Route path="/inquiry" element={<Inquiry />} />

                            {/* feature */}
                            <Route path="/features" element={<Features />} />

                            {/* 경쟁사 분석 */}
                            <Route
                                path="/competitor-landing"
                                element={
                                    <WithoutAuthorization>
                                        <CompetitorLanding />
                                    </WithoutAuthorization>
                                }
                            />
                            <Route
                                path="/competitor"
                                element={
                                    <WithAuthorization>
                                        <CompetitorLayout />
                                    </WithAuthorization>
                                }
                            >
                                <Route path="company" element={<CompanyComparison />} />
                                <Route path="employment" element={<EmploymentComparison />} />
                                <Route path="financial" element={<FinancialComparison />} />
                                <Route path="technology" element={<TechnologyComparison />} />
                                <Route path="news" element={<NewsAnalysis />} />
                                <Route path="view" element={<View />} />
                                <Route path="edit" element={<Edit />} />
                                <Route path="*" element={<Navigate to="/competitor/company" />} />
                            </Route>

                            {/* 해외기업 */}
                            <Route path="/overseas">
                                <Route
                                    path=""
                                    element={
                                        <WithoutAuthorization>
                                            <OverseasLanding />
                                        </WithoutAuthorization>
                                    }
                                />
                                <Route
                                    path="vietnam"
                                    element={
                                        <WithAuthorization>
                                            <VietnamList />
                                        </WithAuthorization>
                                    }
                                />
                                <Route
                                    path="vietnam-interest"
                                    element={
                                        <WithAuthorization>
                                            <VietnamInterest />
                                        </WithAuthorization>
                                    }
                                />
                                <Route
                                    path="*"
                                    element={
                                        <WithAuthorization>
                                            <Navigate to="/overseas" />
                                        </WithAuthorization>
                                    }
                                />
                            </Route>

                            {/* 오픈이노베이션 */}
                            <Route path="/openInnovation">
                                <Route path="" element={<OpenInnovationLanding />} />
                                <Route path="dashboard" element={<OpenInnovationLayout />}>
                                    <Route path="" element={<Dashboard />} />
                                </Route>
                                {/* 협업신청 */}
                                <Route path="technology" element={<OpenInnovationLayout />}>
                                    <Route path="" element={<TechnologyList />} />
                                    <Route
                                        path=":registerId"
                                        element={
                                            <OpenInnovationWithAuthorization>
                                                <TechnologyDetail />
                                            </OpenInnovationWithAuthorization>
                                        }
                                    />
                                    <Route
                                        path="suggest/:registerId"
                                        element={
                                            <OpenInnovationWithAuthorization>
                                                <Suggest />
                                            </OpenInnovationWithAuthorization>
                                        }
                                    />
                                    <Route
                                        path="register"
                                        element={
                                            <OpenInnovationWithAuthorization>
                                                <Register />
                                            </OpenInnovationWithAuthorization>
                                        }
                                    />
                                    <Route path="*" element={<Navigate to="/openInnovation/technology" />} />
                                </Route>
                                {/* 기업찾기 */}
                                <Route path="enterprise" element={<OpenInnovationLayout />}>
                                    <Route path="" element={<EnterpriseList />} />
                                    <Route
                                        path="edit"
                                        element={
                                            <OpenInnovationWithAuthorization>
                                                <EnterpriseEdit />
                                            </OpenInnovationWithAuthorization>
                                        }
                                    />
                                    <Route path="add" element={<EnterpriseAdd />} />
                                    <Route path="content" element={<Content />} />
                                    <Route
                                        path=":participatingId"
                                        element={
                                            <OpenInnovationWithAuthorization>
                                                <EnterpriseDetail />
                                            </OpenInnovationWithAuthorization>
                                        }
                                    />
                                    <Route path="*" element={<Navigate to="/openInnovation/enterprise" />} />
                                </Route>
                                {/* 공지사항 */}
                                <Route path="notice" element={<OpenInnovationLayout />}>
                                    <Route path="" element={<NoticeList />} />
                                    <Route
                                        path=":boardId"
                                        element={
                                            <OpenInnovationWithAuthorization>
                                                <NoticeDetail />
                                            </OpenInnovationWithAuthorization>
                                        }
                                    />
                                    <Route path="*" element={<Navigate to="/openInnovation/notice" />} />
                                </Route>
                                {/* FAQ */}
                                <Route
                                    path="faq"
                                    element={
                                        <OpenInnovationWithAuthorization>
                                            <OpenInnovationLayout />
                                        </OpenInnovationWithAuthorization>
                                    }
                                >
                                    <Route path="" element={<Faq />} />
                                </Route>
                                {/* 나의 협업 현황 */}
                                <Route
                                    path="management"
                                    element={
                                        <OpenInnovationWithAuthorization>
                                            <OpenInnovationLayout />
                                        </OpenInnovationWithAuthorization>
                                    }
                                >
                                    <Route path="" element={<ManagementList />} />
                                    <Route path=":registerId" element={<ManagementDetail />} />
                                    <Route path="technology/:registerId" element={<TechnologyView />} />
                                    <Route path="technology/edit/:registerId" element={<TechnologyEdit />} />
                                    <Route path="proposal/:proposeId" element={<ProposalView />} />
                                    <Route path="proposal/edit/:proposeId" element={<ProposalEdit />} />
                                    <Route path="*" element={<Navigate to="/openInnovation/management" />} />
                                </Route>
                                {/* 관심기술 */}
                                <Route
                                    path="bookmark"
                                    element={
                                        <OpenInnovationWithAuthorization>
                                            <OpenInnovationLayout />
                                        </OpenInnovationWithAuthorization>
                                    }
                                >
                                    <Route path="" element={<BookMark />} />
                                </Route>
                                <Route path="*" element={<Navigate to="/openInnovation/dashboard" />} />
                            </Route>

                            {/* 마이페이지 */}
                            <Route
                                path="/mypage"
                                element={
                                    <WithAuthorization>
                                        <MyPageLayout />
                                    </WithAuthorization>
                                }
                            >
                                <Route path="" element={<Navigate to="/mypage/profile" />} />
                                <Route path="profile" element={<Profile />} />
                                <Route path="profile/edit" element={<ProfileEdit />} />
                                <Route path="password" element={<Password />} />
                                <Route path="message" element={<Message />} />
                                <Route path="message/:inquiryId" element={<MessageView />} />
                                <Route path="messageManagement" element={<MessageManagement />} />
                                <Route path="messageManagement/:inquiryId" element={<MessageManagementView />} />
                                <Route path="interest" element={<Interest />} />
                                <Route path="company" element={<MyCompany />} />
                                <Route path="company/edit" element={<MyCompanyEdit />} />
                                <Route path="product" element={<MyProduct />} />
                                <Route path="product/add" element={<MyProductEdit />} />
                                <Route path="product/edit/:productId" element={<MyProductEdit />} />
                                <Route path="invite" element={<Invite />} />
                                <Route path="authority" element={<Authority />} />
                                <Route path="*" element={<Navigate to="/mypage/profile" />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/" />} />
                        </Routes>
                    </Suspense>
                </SearchProvider>
            </UserProvider>
        </Router>
    );
};

export default Routing;
